/* eslint-disable no-console */
/* eslint-disable no-param-reassign */
import { ProductName } from '@pypestream/api-services';
import { html, nothing, unsafeCSS } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { property, state } from 'lit/decorators.js';
import { TreeUtils } from 'simple-tree-utils';

import { i18n } from '@pypestream/translations';
import { BaseElement, customElement } from '../../components';
import { watch } from '../../components/base-element';
import { TreeItemWC } from '../../components/tree-item/tree-item.wc';
import { handleGoHome, sortByKeyRecursively } from '../utils';
import { toggleFavoriteOrg } from '../utils/favorite-orgs';
import { smartService } from '../xstate/smart.xstate';
import { SmartContext } from '../xstate/smart.xstate-utils';
import { CommonProject, OrgSelectorState, OrgWithChildren } from './types';

import '../../components/buttons/icon-button/icon-button.wc';
import '../../components/icon/icon.wc';
import '../../components/menu/src/menu.wc';
import '../../components/search/search.wc';
import '../../components/segmented-control/segmented-control.wc';
import '../../components/tree/tree.wc';
import './universal-nav-tree-child.wc';

import styles from './universal-nav-org-selector.scss?inline';
import {
  countOrgsInTree,
  countOrgsMatchingMode,
  DEFAULT_ORG_FILTER_MODE,
  effectiveOrgFilterMode,
  filterOrgTreeByMode,
  filterOrgTreeBySearchTerm,
  isOrgFilterOffered,
  OrgMenuFilterMode,
} from '../utils/org-favorites';
import { logos } from '../../assets/logos';

const treeUtils = new TreeUtils({
  idProp: 'id', // the key of a unique identifier for an object (source object)
  parentIdProp: 'parentId', // the key of a unique parent identifier (source object)
  childrenProp: 'children', // the key, where child nodes are stored (destination object tree)
});

@customElement('ps-menu-org-selector')
export class OrgSelectorWC extends BaseElement {
  static styles = unsafeCSS(styles);

  context: SmartContext;

  @property({ type: String }) orgId: string | undefined;

  @property() onSelectionChange: (orgId: string | null) => void;

  @property({ type: Boolean }) isDisabled: boolean = false;

  @property({ reflect: false, type: Boolean }) hideGoHome?: boolean = false;

  @state() private localState: OrgSelectorState = {
    orgs: [],
    orgTree: [],
  };

  @state() private _hasAvailableTools?: boolean = undefined;

  @state() private _accountId?: string = undefined;

  @state() private _assignedProjects?: CommonProject[] = undefined;

  @state() private searchTerm: string = '';

  @state() private filterMode: OrgMenuFilterMode = DEFAULT_ORG_FILTER_MODE;

  @watch(['_hasAvailableTools', '_accountId', '_assignedProjects'])
  handleHasAvailableToolsChange() {
    if (
      this.context?.app !== ProductName.Organization &&
      this._hasAvailableTools &&
      this._accountId &&
      this._assignedProjects
    ) {
      this.onSelectionChange(this._accountId);
    }
  }

  constructor() {
    super();
    this.init = this.init.bind(this);
  }

  connectedCallback() {
    // eslint-disable-next-line wc/guard-super-call
    super.connectedCallback();

    smartService.subscribe((smartState) => {
      const currentUserInfo = this.context?.userInfo;
      const currentHasAvailableTools = this.context?.hasAvailableTools;
      const currentAccountId = this.context?.accountId;
      const assignedProjects = this.context?.assignedProjects;
      const allAssignedProjects = this.context?.allAssignedProjects;

      this.context = smartState.context;

      if (
        currentUserInfo !== smartState.context.userInfo ||
        currentHasAvailableTools !== smartState.context.hasAvailableTools ||
        currentAccountId !== smartState.context.accountId ||
        assignedProjects !== smartState.context.assignedProjects ||
        allAssignedProjects !== smartState.context.allAssignedProjects
      ) {
        this.init();
      }
    });
  }

  init() {
    if (!this.context) return;
    const { userInfo, hasAvailableTools, accountId, assignedProjects } =
      this.context;

    this._hasAvailableTools = hasAvailableTools;

    this._assignedProjects = assignedProjects;

    this._accountId = accountId;

    const defaultAccount = userInfo?.defaultAccount;

    if (defaultAccount) {
      const { allChildAccounts, ...orgProps } = defaultAccount;

      this.localState.orgs = [orgProps].concat(allChildAccounts || []);
    }

    const orgsWithChildren: OrgWithChildren[] = treeUtils.list2Tree(
      (this.localState.orgs || []).map((org) => {
        if (org.id === this.context?.userInfo?.defaultAccount?.id) {
          return { ...org, parentId: null };
        }
        return org;
      }) || []
    ) as OrgWithChildren[];

    orgsWithChildren.map((org) => {
      if ('id' in org) {
        org.selected = org?.id === this.orgId;
      }

      if ('children' in org === undefined) {
        org.children = [];
      }

      return org;
    }) as OrgWithChildren[];

    const maxDepth = (root: OrgWithChildren) => {
      // if root is undefined, depth is 0
      if (!root) return 0;
      // variable to store the maximum levels
      let max = 0;
      // helper function to traverse the tree
      // recursively increment the levels by one
      const dfs = (node: OrgWithChildren, levels: number) => {
        // console.log(node.name, levels);

        node.depth = levels;
        // compare levels and max to pass the maximum levels
        if (levels > max) max = levels;
        // traverse all children of the current node
        for (const child of node.children) {
          // increment the levels by one
          dfs(child, levels + 1);
        }
      };
      // when root is not null, the tree has at least one level,
      // so we pass down 1
      dfs(root, 1);

      // return the maximum levels
      return max;
    };

    maxDepth(orgsWithChildren[0]);

    this.localState = {
      ...this.localState,
      orgTree: orgsWithChildren,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  private handleFavoriteToggle({
    detail: { orgId },
  }: CustomEvent<{ orgId: string }>) {
    // Shared with every other caller (e.g. the Manager roles-table star,
    // PSTUDIO-3330 Unit 2) so a toggle from either surface is instantly
    // consistent with the other — one write path, not two.
    toggleFavoriteOrg(orgId);
  }

  // eslint-disable-next-line class-methods-use-this
  renderGoToHome() {
    if (this.hideGoHome) return nothing;

    return html`
      <div class="c-u-nav-org-selector__home-button">
        <ps-button
          variant="ghost"
          width="full"
          size="large"
          @click=${() =>
            handleGoHome({
              accountId: this.context?.accountId,
              app: this.context?.app,
            })}
          id="go-home-button"
        >
          ${i18n.t('navigation:mega.sidebar.goHome') || 'Go Home'}
          <ps-image
            src=${logos.home}
            width="24px"
            height="24px"
            slot="prefix"
            radius="small"
          ></ps-image>
        </ps-button>
      </div>
    `;
  }

  render() {
    const { orgTree, orgs } = this.localState;

    const topOrgname = 'Pypestream';
    const sortingKey = 'name';

    const pypeOrg = orgs?.filter((org) => org.name === topOrgname) || [];
    const restOrgs = sortByKeyRecursively(
      orgs?.filter((org) => org.name !== topOrgname) || [],
      sortingKey
    );
    const sortedOrgs = pypeOrg.concat(restOrgs);

    const treeState = orgTree?.length
      ? sortByKeyRecursively(orgTree, sortingKey)
      : sortedOrgs;

    const primaryOrgId = this.context?.userInfo?.defaultAccount?.id;
    const favoriteOrgIds: string[] =
      this.context?.userInfo?.settings?.favoriteOrgIds || [];

    const totalOrgsCount = countOrgsInTree(treeState);
    const myOrgsCount = countOrgsMatchingMode(treeState, {
      favoriteOrgIds,
      mode: 'my',
      primaryOrgId,
    });

    // A user with one org has nothing to switch between, so the segmented
    // control is not rendered and the stored mode must not apply either — a
    // hidden 'my' would otherwise filter a view nobody can change.
    const showFilter = isOrgFilterOffered(totalOrgsCount);
    const filterMode = effectiveOrgFilterMode(this.filterMode, totalOrgsCount);

    const searchedTree = filterOrgTreeBySearchTerm(treeState, this.searchTerm);
    const filteredTree = filterOrgTreeByMode(searchedTree, {
      favoriteOrgIds,
      mode: filterMode,
      primaryOrgId,
    });

    // An empty tree is only explained when a filter caused it — with no
    // search term and "All Orgs" in effect an empty list is the unfiltered
    // empty state, which is left as it was.
    const isFilteredEmpty =
      filteredTree.length === 0 && (!!this.searchTerm || filterMode !== 'all');

    return html`
      <div class="c-u-nav-org-selector">
        ${this.renderGoToHome()}
        <ps-search
          class="c-u-nav-org-selector__search"
          size="medium"
          placeholder=${i18n.t('navigation:orgMenu.searchPlaceholder', {
            count: totalOrgsCount,
          }) || `Search all ${totalOrgsCount} Orgs`}
          .value=${this.searchTerm}
          @input=${(event: Event) => {
            this.searchTerm = (event.target as { value?: string }).value ?? '';
          }}
        ></ps-search>
        ${showFilter
          ? html`<ps-segmented-control
              class="c-u-nav-org-selector__filter"
              value=${this.filterMode}
              @change=${({
                detail: { value },
              }: {
                detail: { value: OrgMenuFilterMode };
              }) => {
                this.filterMode = value;
              }}
            >
              <ps-segmented-control-item value="my">
                ${i18n.t('navigation:orgMenu.myOrgs', { count: myOrgsCount }) ||
                `My Orgs ${myOrgsCount}`}
              </ps-segmented-control-item>
              <ps-segmented-control-item value="all">
                ${i18n.t('navigation:orgMenu.allOrgs', {
                  count: totalOrgsCount,
                }) || `All Orgs ${totalOrgsCount}`}
              </ps-segmented-control-item>
            </ps-segmented-control> `
          : nothing}
        <div class="c-u-nav-org-selector__status" role="status">
          ${isFilteredEmpty
            ? html`<p class="c-u-nav-org-selector__status-text">
                ${i18n.t('navigation:orgMenu.noResults') ||
                'No orgs match your search or filter'}
              </p>`
            : nothing}
        </div>
        <ps-tree
          class="c-u-nav-org-selector__tree"
          @favorite-toggle=${(event: CustomEvent<{ orgId: string }>) =>
            this.handleFavoriteToggle(event)}
          @pl-selection-change=${({
            detail: { selection },
          }: {
            detail: { selection: TreeItemWC[] };
          }) => {
            const newOrgId = selection[0]?.getAttribute('data-org-id');

            if (!newOrgId) {
              throw new Error('No org id found');
            }

            if (this.context?.app === ProductName.Organization) {
              this.onSelectionChange?.(newOrgId);
            } else {
              smartService.send({
                type: 'changeOrgSelection',
                orgId: newOrgId,
              });
            }
          }}
        >
          ${filteredTree?.map(
            (item) => html`
              <ps-universal-nav-tree-child
                .child=${item}
                orgId=${ifDefined(
                  this.context?.changeOrgSelection || this.orgId
                )}
                ?isDisabled=${this.isDisabled}
                .favoriteOrgIds=${favoriteOrgIds}
                primaryOrgId=${ifDefined(primaryOrgId)}
              ></ps-universal-nav-tree-child>
            `
          )}
        </ps-tree>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-menu-org-selector': OrgSelectorWC;
  }
  enum PSElementTagNameMap {
    'ps-menu-org-selector' = 'ps-menu-org-selector',
  }
}
